// First Created - 08 Oct 2026
// Author - Atul Pandey
// Purpose - Admin list page for Org Trail Session Content Assignments
//
// Updated - 11 Oct 2026
// - Added Bulk Cohort Assignment
// - Added Org, Trail, Session and Cohort destination selectors
// - Added searchable reference content selection
// - Added duplicate and inactive assignment detection
// - Added sequential bulk creation and progress reporting
// - Retained existing grouped assignment list and CRUD actions


import React, {
    useMemo,
    useState,
} from "react"

import useTitle from "@/hooks/useTitle"

import {
    addIcon,
} from "@/assets/img/imgAssets"

import {
    PlusIcon,
    MinusIcon,
    TrashIcon,
    PencilSquareIcon,
    MagnifyingGlassIcon,
    XMarkIcon,
    CheckCircleIcon,
    ExclamationTriangleIcon,
    RectangleStackIcon as RectangleStackOutlineIcon,
} from "@heroicons/react/24/outline"

import {
    BuildingOfficeIcon,
    AcademicCapIcon,
    UserGroupIcon,
    RectangleStackIcon,
    PlayCircleIcon,
    DocumentTextIcon,
    BookOpenIcon,
} from "@heroicons/react/24/solid"

import PulseLoader from "react-spinners/PulseLoader"

import {
    useGetOrganizationsQuery,
} from "@/pages/admin/organizations/organizationsApiSlice"

import {
    useGetTrailsQuery,
} from "@/features/trails/trailsApiSlice"

import {
    useGetSessionsQuery,
} from "@/features/sessions/sessionsApiSlice"

import {
    useGetOrgTrailSessionCohortsQuery,
} from "@/pages/admin/orgTrailSessionCohorts/orgTrailSessionCohortsApiSlice"

import {
    useGetSessionReferenceContentQuery,
} from "@/pages/admin/sessionReferenceContent/sessionReferenceContentApiSlice"

import {
    useGetOrgTrailSessionContentAssignmentsQuery,
    useCreateOrgTrailSessionContentAssignmentMutation,
} from "./orgTrailSessionContentAssignmentsApiSlice"



/*
|--------------------------------------------------------------------------
| 01 - Helpers
|--------------------------------------------------------------------------
*/

const getRefId = value => {

    if (!value) return ""

    if (typeof value === "string") {
        return value
    }

    return String(
        value?._id ||
        value?.id ||
        ""
    )

}


const toList = data => {

    return (
        data?.ids || []
    )
        .map(
            id => data.entities?.[id]
        )
        .filter(Boolean)

}


const getContentIcon = contentType => {

    switch (contentType) {

        case "Video":
            return PlayCircleIcon

        case "Article":
            return DocumentTextIcon

        default:
            return BookOpenIcon

    }

}



/*
|--------------------------------------------------------------------------
| 02 - Main Component
|--------------------------------------------------------------------------
*/

const EtOrgTrailSessionContentAssignmentList = ({
    onAdd,
    onUpdate,
    onDelete,
}) => {

    useTitle(
        "Session Content Assignments"
    )


    /*
    |--------------------------------------------------------------------------
    | 03 - Existing Group Expansion
    |--------------------------------------------------------------------------
    */

    const [
        expanded,
        setExpanded,
    ] = useState({})


    const toggle = key => {

        setExpanded(previous => ({

            ...previous,

            [key]: !previous[key],

        }))

    }


    /*
    |--------------------------------------------------------------------------
    | 04 - Bulk Assignment Visibility
    |--------------------------------------------------------------------------
    */

    const [
        showBulkAssign,
        setShowBulkAssign,
    ] = useState(false)


    /*
    |--------------------------------------------------------------------------
    | 05 - Bulk Assignment Destination
    |--------------------------------------------------------------------------
    */

    const [
        bulkOrgId,
        setBulkOrgId,
    ] = useState("")

    const [
        bulkTrailId,
        setBulkTrailId,
    ] = useState("")

    const [
        bulkSessionId,
        setBulkSessionId,
    ] = useState("")

    const [
        bulkCohortId,
        setBulkCohortId,
    ] = useState("")


    /*
    |--------------------------------------------------------------------------
    | 06 - Bulk Content Selection
    |--------------------------------------------------------------------------
    */

    const [
        bulkSearchText,
        setBulkSearchText,
    ] = useState("")

    const [
        selectedContentIds,
        setSelectedContentIds,
    ] = useState([])


    /*
    |--------------------------------------------------------------------------
    | 07 - Bulk Processing State
    |--------------------------------------------------------------------------
    */

    const [
        isBulkAssigning,
        setIsBulkAssigning,
    ] = useState(false)

    const [
        bulkError,
        setBulkError,
    ] = useState("")

    const [
        bulkProgress,
        setBulkProgress,
    ] = useState(null)

    const [
        bulkResults,
        setBulkResults,
    ] = useState([])

    const [
        bulkSkippedCounts,
        setBulkSkippedCounts,
    ] = useState(null)


    /*
    |--------------------------------------------------------------------------
    | 08 - Queries
    |--------------------------------------------------------------------------
    */

    const {
        data: assignmentsData,
        isLoading,
        isError,
        error,
        refetch: refetchAssignments,
    } = useGetOrgTrailSessionContentAssignmentsQuery()


    const {
        data: organizationsData,
    } = useGetOrganizationsQuery(
        "organizationsList"
    )


    const {
        data: trailsData,
    } = useGetTrailsQuery(
        "trailsList"
    )


    const {
        data: sessionsData,
    } = useGetSessionsQuery(
        "sessionsList"
    )


    const {
        data: cohortsData,
    } = useGetOrgTrailSessionCohortsQuery({})


    /*
    |--------------------------------------------------------------------------
    | 09 - Master Reference Content Query
    |--------------------------------------------------------------------------
    |
    | This is the same master content collection used by
    | the Session Reference Content admin page.
    |
    | Only reference content belonging to the selected
    | Trinity Session will be available for bulk assignment.
    |
    |--------------------------------------------------------------------------
    */

    const {
        data: referenceContentData,
        isLoading: isContentLoading,
        isError: isContentError,
        error: contentError,
    } = useGetSessionReferenceContentQuery()


    /*
    |--------------------------------------------------------------------------
    | 10 - Create Assignment Mutation
    |--------------------------------------------------------------------------
    */

    const [
        createContentAssignment,
    ] = useCreateOrgTrailSessionContentAssignmentMutation()


    /*
    |--------------------------------------------------------------------------
    | 11 - Normalize Collections
    |--------------------------------------------------------------------------
    */

    const assignments = useMemo(
        () => toList(assignmentsData),
        [assignmentsData]
    )

    const organizations = useMemo(
        () => toList(organizationsData),
        [organizationsData]
    )

    const trails = useMemo(
        () => toList(trailsData),
        [trailsData]
    )

    const sessions = useMemo(
        () => toList(sessionsData),
        [sessionsData]
    )

    const cohorts = useMemo(
        () => toList(cohortsData),
        [cohortsData]
    )

    const referenceContent = useMemo(
        () => toList(referenceContentData),
        [referenceContentData]
    )


    /*
    |--------------------------------------------------------------------------
    | 12 - Resolve Reference Names
    |--------------------------------------------------------------------------
    */

    const findName = (
        list,
        id,
        field
    ) => {

        const item = list.find(
            record =>
                getRefId(record) ===
                getRefId(id)
        )

        return item?.[field] || ""

    }


    /*
    |--------------------------------------------------------------------------
    | 13 - Bulk Destination Organization Options
    |--------------------------------------------------------------------------
    */

    const bulkOrgOptions = useMemo(
        () => {

            return organizations
                .filter(
                    org =>
                        org.active !== false
                )
                .slice()
                .sort(
                    (a, b) =>
                        (
                            a.organizationName || ""
                        ).localeCompare(
                            b.organizationName || ""
                        )
                )

        },
        [organizations]
    )


    /*
    |--------------------------------------------------------------------------
    | 14 - Bulk Destination Trail Options
    |--------------------------------------------------------------------------
    |
    | Trails are selected from the master Trail collection.
    | Cohorts are subsequently restricted by Org + Trail.
    |
    |--------------------------------------------------------------------------
    */

    const bulkTrailOptions = useMemo(
        () => {

            return trails
                .filter(
                    trail =>
                        trail.active !== false
                )
                .slice()
                .sort(
                    (a, b) =>
                        (
                            a.trailName || ""
                        ).localeCompare(
                            b.trailName || ""
                        )
                )

        },
        [trails]
    )


    /*
    |--------------------------------------------------------------------------
    | 15 - Bulk Destination Session Options
    |--------------------------------------------------------------------------
    |
    | The selected Session must belong to the chosen Trail.
    |
    |--------------------------------------------------------------------------
    */

    const bulkSessionOptions = useMemo(
        () => {

            if (!bulkTrailId) {
                return []
            }

            return sessions
                .filter(
                    session =>

                        session.active !== false &&

                        getRefId(
                            session.trailId
                        ) === bulkTrailId

                )
                .slice()
                .sort(
                    (a, b) => {

                        const orderDifference =
                            (
                                a.sessionSequence ?? 999999
                            ) -
                            (
                                b.sessionSequence ?? 999999
                            )

                        if (
                            orderDifference !== 0
                        ) {

                            return orderDifference

                        }

                        return (
                            a.sessionName || ""
                        ).localeCompare(
                            b.sessionName || ""
                        )

                    }
                )

        },
        [
            sessions,
            bulkTrailId,
        ]
    )


    /*
    |--------------------------------------------------------------------------
    | 16 - Bulk Destination Cohort Options
    |--------------------------------------------------------------------------
    |
    | Cohorts are authoritative for Org + Trail membership.
    |
    | Cohort.sessionId is not used here.
    |
    |--------------------------------------------------------------------------
    */

    const bulkCohortOptions = useMemo(
        () => {

            if (
                !bulkOrgId ||
                !bulkTrailId
            ) {
                return []
            }

            return cohorts
                .filter(
                    cohort =>

                        cohort.active !== false &&

                        getRefId(
                            cohort.orgId
                        ) === bulkOrgId &&

                        getRefId(
                            cohort.trailId
                        ) === bulkTrailId

                )
                .slice()
                .sort(
                    (a, b) => {

                        const nameA =
                            a.cohortName ||
                            a.cohortCode ||
                            ""

                        const nameB =
                            b.cohortName ||
                            b.cohortCode ||
                            ""

                        return nameA.localeCompare(
                            nameB
                        )

                    }
                )

        },
        [
            cohorts,
            bulkOrgId,
            bulkTrailId,
        ]
    )


    /*
    |--------------------------------------------------------------------------
    | 17 - Reference Content For Selected Session
    |--------------------------------------------------------------------------
    |
    | Only active master content is eligible for new
    | assignments.
    |
    |--------------------------------------------------------------------------
    */

    const eligibleReferenceContent = useMemo(
        () => {

            if (!bulkSessionId) {
                return []
            }

            return referenceContent
                .filter(
                    content =>

                        content.active !== false &&

                        getRefId(
                            content.sessionId
                        ) === bulkSessionId

                )
                .slice()
                .sort(
                    (a, b) => {

                        const orderDifference =
                            (
                                a.displayOrder ?? 0
                            ) -
                            (
                                b.displayOrder ?? 0
                            )

                        if (
                            orderDifference !== 0
                        ) {

                            return orderDifference

                        }

                        return (
                            a.title || ""
                        ).localeCompare(
                            b.title || ""
                        )

                    }
                )

        },
        [
            referenceContent,
            bulkSessionId,
        ]
    )


    /*
    |--------------------------------------------------------------------------
    | 18 - Search Eligible Reference Content
    |--------------------------------------------------------------------------
    */

    const filteredReferenceContent = useMemo(
        () => {

            const search =
                bulkSearchText
                    .trim()
                    .toLowerCase()

            if (!search) {

                return eligibleReferenceContent

            }

            return eligibleReferenceContent.filter(
                content => {

                    const searchable = [

                        content.title,
                        content.description,
                        content.contentType,

                    ]
                        .filter(Boolean)
                        .join(" ")
                        .toLowerCase()

                    return searchable.includes(
                        search
                    )

                }
            )

        },
        [
            eligibleReferenceContent,
            bulkSearchText,
        ]
    )


    /*
    |--------------------------------------------------------------------------
    | 19 - Existing Assignments For Selected Destination
    |--------------------------------------------------------------------------
    |
    | Match the complete assignment combination:
    |
    | Org + Trail + Session + Cohort + Reference Content
    |
    | Inactive records are included to avoid attempting
    | to create a duplicate of an inactive assignment.
    |
    |--------------------------------------------------------------------------
    */

    const existingAssignmentMap = useMemo(
        () => {

            const map = new Map()

            if (
                !bulkOrgId ||
                !bulkTrailId ||
                !bulkSessionId ||
                !bulkCohortId
            ) {

                return map

            }

            assignments.forEach(
                assignment => {

                    if (

                        getRefId(
                            assignment.orgId
                        ) !== bulkOrgId ||

                        getRefId(
                            assignment.trailId
                        ) !== bulkTrailId ||

                        getRefId(
                            assignment.sessionId
                        ) !== bulkSessionId ||

                        getRefId(
                            assignment.cohortId
                        ) !== bulkCohortId

                    ) {

                        return

                    }

                    const referenceContentId =
                        getRefId(
                            assignment.referenceContentId
                        )

                    if (referenceContentId) {

                        map.set(
                            referenceContentId,
                            assignment
                        )

                    }

                }
            )

            return map

        },
        [
            assignments,
            bulkOrgId,
            bulkTrailId,
            bulkSessionId,
            bulkCohortId,
        ]
    )


    /*
    |--------------------------------------------------------------------------
    | 20 - Content Assignment Status
    |--------------------------------------------------------------------------
    */

    const getAssignmentStatus = contentId => {

        const existing =
            existingAssignmentMap.get(
                contentId
            )

        if (!existing) {
            return "Available"
        }

        if (
            existing.active === false
        ) {
            return "Inactive"
        }

        return "Assigned"

    }


    /*
    |--------------------------------------------------------------------------
    | 21 - Available Content
    |--------------------------------------------------------------------------
    |
    | Already assigned and inactive existing records
    | cannot be selected for creation.
    |
    |--------------------------------------------------------------------------
    */

    const availableContent = useMemo(
        () => {

            return eligibleReferenceContent.filter(
                content =>
                    !existingAssignmentMap.has(
                        getRefId(content)
                    )
            )

        },
        [
            eligibleReferenceContent,
            existingAssignmentMap,
        ]
    )


    /*
    |--------------------------------------------------------------------------
    | 22 - Selected Content Records
    |--------------------------------------------------------------------------
    */

    const selectedContent = useMemo(
        () => {

            const selectedIds =
                new Set(
                    selectedContentIds
                )

            return availableContent.filter(
                content =>
                    selectedIds.has(
                        getRefId(content)
                    )
            )

        },
        [
            availableContent,
            selectedContentIds,
        ]
    )


    /*
    |--------------------------------------------------------------------------
    | 23 - Assignment Counts
    |--------------------------------------------------------------------------
    */

    const bulkCounts = useMemo(
        () => {

            const assigned =
                eligibleReferenceContent.filter(
                    content => {

                        const existing =
                            existingAssignmentMap.get(
                                getRefId(content)
                            )

                        return (
                            existing &&
                            existing.active !== false
                        )

                    }
                ).length

            const inactive =
                eligibleReferenceContent.filter(
                    content => {

                        const existing =
                            existingAssignmentMap.get(
                                getRefId(content)
                            )

                        return (
                            existing &&
                            existing.active === false
                        )

                    }
                ).length

            return {

                total:
                    eligibleReferenceContent.length,

                available:
                    availableContent.length,

                assigned,

                inactive,

                selected:
                    selectedContent.length,

            }

        },
        [
            eligibleReferenceContent,
            availableContent,
            selectedContent,
            existingAssignmentMap,
        ]
    )


    /*
    |--------------------------------------------------------------------------
    | 24 - Destination Completeness
    |--------------------------------------------------------------------------
    */

    const destinationReady =

        Boolean(bulkOrgId) &&

        Boolean(bulkTrailId) &&

        Boolean(bulkSessionId) &&

        Boolean(bulkCohortId) &&

        bulkSessionOptions.some(
            session =>
                getRefId(session) ===
                bulkSessionId
        ) &&

        bulkCohortOptions.some(
            cohort =>
                getRefId(cohort) ===
                bulkCohortId
        )


    /*
    |--------------------------------------------------------------------------
    | 25 - Reset Selection And Results
    |--------------------------------------------------------------------------
    */

    const resetBulkSelection = () => {

        setSelectedContentIds([])
        setBulkSearchText("")
        setBulkError("")
        setBulkProgress(null)
        setBulkResults([])
        setBulkSkippedCounts(null)

    }


    /*
    |--------------------------------------------------------------------------
    | 26 - Open / Close Bulk Assignment
    |--------------------------------------------------------------------------
    */

    const toggleBulkAssign = () => {

        if (isBulkAssigning) {
            return
        }

        if (showBulkAssign) {

            resetBulkSelection()

        }

        setShowBulkAssign(
            previous => !previous
        )

    }


    /*
    |--------------------------------------------------------------------------
    | 27 - Destination Selection Handlers
    |--------------------------------------------------------------------------
    |
    | Changing a destination dimension clears dependent
    | selections and previous processing results.
    |
    |--------------------------------------------------------------------------
    */

    const handleOrgChange = orgId => {

        if (isBulkAssigning) return

        setBulkOrgId(orgId)
        setBulkCohortId("")

        resetBulkSelection()

    }


    const handleTrailChange = trailId => {

        if (isBulkAssigning) return

        setBulkTrailId(trailId)
        setBulkSessionId("")
        setBulkCohortId("")

        resetBulkSelection()

    }


    const handleSessionChange = sessionId => {

        if (isBulkAssigning) return

        setBulkSessionId(sessionId)

        resetBulkSelection()

    }


    const handleCohortChange = cohortId => {

        if (isBulkAssigning) return

        setBulkCohortId(cohortId)

        resetBulkSelection()

    }


    /*
    |--------------------------------------------------------------------------
    | 28 - Toggle Individual Content Selection
    |--------------------------------------------------------------------------
    */

    const toggleContentSelection = contentId => {

        if (
            isBulkAssigning ||
            !destinationReady ||
            existingAssignmentMap.has(contentId)
        ) {
            return
        }

        setSelectedContentIds(
            previous => {

                if (
                    previous.includes(contentId)
                ) {

                    return previous.filter(
                        id => id !== contentId
                    )

                }

                return [
                    ...previous,
                    contentId,
                ]

            }
        )

    }


    /*
    |--------------------------------------------------------------------------
    | 29 - Select All Visible
    |--------------------------------------------------------------------------
    |
    | Selects only available records currently shown
    | by the search filter.
    |
    |--------------------------------------------------------------------------
    */

    const handleSelectAllVisible = () => {

        if (
            isBulkAssigning ||
            !destinationReady
        ) {
            return
        }

        const availableVisibleIds =
            filteredReferenceContent
                .filter(
                    content =>
                        !existingAssignmentMap.has(
                            getRefId(content)
                        )
                )
                .map(
                    content =>
                        getRefId(content)
                )

        setSelectedContentIds(
            previous => [

                ...new Set([
                    ...previous,
                    ...availableVisibleIds,
                ]),

            ]
        )

    }


    /*
    |--------------------------------------------------------------------------
    | 30 - Clear Selection
    |--------------------------------------------------------------------------
    */

    const handleClearSelection = () => {

        if (isBulkAssigning) return

        setSelectedContentIds([])

    }


    /*
    |--------------------------------------------------------------------------
    | 31 - Bulk Assignment Processing
    |--------------------------------------------------------------------------
    |
    | Uses existing single record creation mutation.
    |
    | Rechecks the latest assignments before starting.
    |
    | Creates assignments sequentially.
    |
    | Continues processing if an individual row fails.
    |
    |--------------------------------------------------------------------------
    */

    const handleBulkAssign = async () => {

        if (
            isBulkAssigning ||
            !destinationReady ||
            selectedContent.length === 0
        ) {
            return
        }

        const itemsToProcess = [
            ...selectedContent,
        ]

        const destination = {

            orgId: bulkOrgId,

            trailId: bulkTrailId,

            sessionId: bulkSessionId,

            cohortId: bulkCohortId,

        }

        setIsBulkAssigning(true)

        setBulkError("")
        setBulkResults([])
        setBulkSkippedCounts(null)

        setBulkProgress({

            completed: 0,

            total: itemsToProcess.length,

        })

        const results = []

        let skippedAssigned = 0
        let skippedInactive = 0

        try {

            /*
            |--------------------------------------------------------------------------
            | Refresh Existing Assignments Before Import
            |--------------------------------------------------------------------------
            */

            const refreshed =
                await refetchAssignments().unwrap()

            const currentAssignments =
                toList(refreshed)

            const latestAssignmentMap =
                new Map()

            currentAssignments.forEach(
                assignment => {

                    if (

                        getRefId(
                            assignment.orgId
                        ) !== destination.orgId ||

                        getRefId(
                            assignment.trailId
                        ) !== destination.trailId ||

                        getRefId(
                            assignment.sessionId
                        ) !== destination.sessionId ||

                        getRefId(
                            assignment.cohortId
                        ) !== destination.cohortId

                    ) {

                        return

                    }

                    latestAssignmentMap.set(

                        getRefId(
                            assignment.referenceContentId
                        ),

                        assignment

                    )

                }
            )


            /*
            |--------------------------------------------------------------------------
            | Process Selected Content
            |--------------------------------------------------------------------------
            */

            for (
                let index = 0;
                index < itemsToProcess.length;
                index++
            ) {

                const content =
                    itemsToProcess[index]

                const contentId =
                    getRefId(content)

                const existing =
                    latestAssignmentMap.get(
                        contentId
                    )

                if (existing) {

                    if (
                        existing.active === false
                    ) {

                        skippedInactive++

                        results.push({

                            contentId,

                            title:
                                content.title,

                            status:
                                "Inactive Existing",

                            message:
                                "An inactive assignment already exists. Reactivate it using the Update action.",

                        })

                    } else {

                        skippedAssigned++

                        results.push({

                            contentId,

                            title:
                                content.title,

                            status:
                                "Already Assigned",

                            message:
                                "Content is already assigned to this cohort.",

                        })

                    }

                } else {

                    const payload = {

                        orgId:
                            destination.orgId,

                        trailId:
                            destination.trailId,

                        sessionId:
                            destination.sessionId,

                        cohortId:
                            destination.cohortId,

                        referenceContentId:
                            contentId,

                        active:
                            true,

                    }

                    try {

                        await createContentAssignment(
                            payload
                        ).unwrap()

                        results.push({

                            contentId,

                            title:
                                content.title,

                            status:
                                "Created",

                            message:
                                "Assignment created successfully.",

                        })

                        /*
                        |--------------------------------------------------------------------------
                        | Local Duplicate Guard
                        |--------------------------------------------------------------------------
                        |
                        | Prevent another attempt for the same ID
                        | during this processing run.
                        |
                        |--------------------------------------------------------------------------
                        */

                        latestAssignmentMap.set(
                            contentId,
                            {
                                ...payload,
                            }
                        )

                    } catch (error) {

                        results.push({

                            contentId,

                            title:
                                content.title,

                            status:
                                "Failed",

                            message:
                                error?.data?.message ||
                                error?.message ||
                                "Unable to create assignment.",

                        })

                    }

                }

                setBulkProgress({

                    completed:
                        index + 1,

                    total:
                        itemsToProcess.length,

                })

            }

            setBulkSkippedCounts({

                assigned:
                    skippedAssigned,

                inactive:
                    skippedInactive,

            })

            setBulkResults(results)

        } catch (error) {

            setBulkError(

                error?.data?.message ||
                error?.message ||
                "Unable to refresh assignments before processing."

            )

        } finally {

            setIsBulkAssigning(false)

        }

    }


    /*
    |--------------------------------------------------------------------------
    | 32 - Group Assignments
    |--------------------------------------------------------------------------
    |
    | Organization
    |   Trail
    |     Cohort
    |       Session
    |         Reference Content
    |
    | Existing hierarchy retained.
    |
    |--------------------------------------------------------------------------
    */

    const groupedAssignments = useMemo(() => {

        const groups = {}

        assignments.forEach(
            assignment => {

                const orgId =
                    getRefId(
                        assignment.orgId
                    )

                const trailId =
                    getRefId(
                        assignment.trailId
                    )

                const cohortId =
                    getRefId(
                        assignment.cohortId
                    )

                const sessionId =
                    getRefId(
                        assignment.sessionId
                    )


                /*
                |--------------------------------------------------------------------------
                | Organization
                |--------------------------------------------------------------------------
                */

                if (!groups[orgId]) {

                    groups[orgId] = {

                        id:
                            orgId,

                        name:
                            findName(
                                organizations,
                                orgId,
                                "organizationName"
                            ) ||
                            "Organization not defined",

                        trails: {},

                        count: 0,

                    }

                }

                const org =
                    groups[orgId]


                /*
                |--------------------------------------------------------------------------
                | Trail
                |--------------------------------------------------------------------------
                */

                if (
                    !org.trails[trailId]
                ) {

                    org.trails[trailId] = {

                        id:
                            trailId,

                        name:
                            findName(
                                trails,
                                trailId,
                                "trailName"
                            ) ||
                            "Trail not defined",

                        cohorts: {},

                        count: 0,

                    }

                }

                const trail =
                    org.trails[trailId]


                /*
                |--------------------------------------------------------------------------
                | Cohort
                |--------------------------------------------------------------------------
                */

                if (
                    !trail.cohorts[cohortId]
                ) {

                    const cohort =
                        cohorts.find(
                            item =>
                                getRefId(item) ===
                                cohortId
                        )

                    trail.cohorts[cohortId] = {

                        id:
                            cohortId,

                        name:
                            cohort?.cohortName ||
                            cohort?.cohortCode ||
                            "Cohort not defined",

                        sessions: {},

                        count: 0,

                    }

                }

                const cohort =
                    trail.cohorts[cohortId]


                /*
                |--------------------------------------------------------------------------
                | Session
                |--------------------------------------------------------------------------
                */

                if (
                    !cohort.sessions[sessionId]
                ) {

                    cohort.sessions[sessionId] = {

                        id:
                            sessionId,

                        name:
                            findName(
                                sessions,
                                sessionId,
                                "sessionName"
                            ) ||
                            "Session not defined",

                        assignments: [],

                        count: 0,

                    }

                }

                const session =
                    cohort.sessions[sessionId]


                /*
                |--------------------------------------------------------------------------
                | Assignment
                |--------------------------------------------------------------------------
                */

                session.assignments.push(
                    assignment
                )

                org.count++
                trail.count++
                cohort.count++
                session.count++

            }
        )

        return Object.values(groups)
            .sort(
                (a, b) =>
                    a.name.localeCompare(
                        b.name
                    )
            )

    }, [
        assignments,
        organizations,
        trails,
        sessions,
        cohorts,
    ])


    /*
    |--------------------------------------------------------------------------
    | 33 - Expandable Group Header
    |--------------------------------------------------------------------------
    */

    const GroupHeader = ({
        groupKey,
        icon: Icon,
        title,
        count,
        className = "",
    }) => (

        <div
            className={`
                flex items-center justify-between
                rounded p-2 gap-2
                ${className}
            `}
        >

            <div className="flex items-center min-w-0">

                <button
                    type="button"
                    onClick={() =>
                        toggle(groupKey)
                    }
                    aria-expanded={
                        !!expanded[groupKey]
                    }
                    className="rounded-full p-1 hover:bg-white"
                >

                    {expanded[groupKey] ? (

                        <MinusIcon
                            className="h-5 w-5 text-orange-600"
                        />

                    ) : (

                        <PlusIcon
                            className="h-5 w-5 text-orange-600"
                        />

                    )}

                </button>

                <Icon
                    className="h-5 w-5 text-orange-600 mx-2 shrink-0"
                />

                <p className="text-xs font-bold truncate">

                    {title}

                </p>

            </div>

            <span className="text-[10px] bg-orange-100 text-orange-700 rounded-full px-3 py-1 mx-2 shrink-0">

                Assignments: {count}

            </span>

        </div>

    )


    /*
    |--------------------------------------------------------------------------
    | 34 - Loading / Error
    |--------------------------------------------------------------------------
    */

    if (isLoading) {

        return (
            <PulseLoader color="#ea580c" />
        )

    }

    if (isError) {

        return (

            <p className="text-sm text-orange-700">

                {error?.data?.message ||
                    "Unable to load Session Content assignments."}

            </p>

        )

    }


    /*
    |--------------------------------------------------------------------------
    | 35 - Render
    |--------------------------------------------------------------------------
    */

    return (

        <div className="bg-gray-200 m-1 p-1 rounded font-poppins mx-auto">


            {/*
            |--------------------------------------------------------------------------
            | 35a - Header
            |--------------------------------------------------------------------------
            */}

            <div className="flex flex-wrap justify-between items-center gap-2 m-1 p-1">

                <div className="flex flex-wrap items-center gap-2">

                    <p className="text-2xl m-1 p-1 font-bold">

                        Session Content Assignments

                    </p>

                    <img
                        src={addIcon}
                        alt="Add"
                        className="h-10 w-10 cursor-pointer"
                        onClick={onAdd}
                    />


                    {/*
                    |--------------------------------------------------------------------------
                    | Bulk Assign Button
                    |--------------------------------------------------------------------------
                    */}

                    <button
                        type="button"
                        disabled={isBulkAssigning}
                        onClick={
                            toggleBulkAssign
                        }
                        className="
                            inline-flex items-center gap-2
                            bg-orange-100
                            text-orange-700
                            border border-orange-300
                            rounded-full
                            px-4 py-2
                            text-xs font-bold
                            hover:bg-orange-200
                            disabled:opacity-50
                        "
                    >

                        <RectangleStackOutlineIcon
                            className="h-4 w-4"
                        />

                        Bulk Assign Content

                    </button>

                </div>


                <p className="text-xs bg-white border border-orange-600 text-orange-600 rounded-full px-3 py-1">

                    Total: {assignments.length}

                </p>

            </div>



            {/*
            |--------------------------------------------------------------------------
            | 36 - Bulk Cohort Assignment Panel
            |--------------------------------------------------------------------------
            */}

            {showBulkAssign && (

                <div className="bg-white rounded-xl border border-orange-200 m-1 p-4 space-y-4">


                    {/*
                    |--------------------------------------------------------------------------
                    | Bulk Assignment Header
                    |--------------------------------------------------------------------------
                    */}

                    <div className="flex items-start justify-between gap-2">

                        <div>

                            <p className="text-base font-bold text-gray-800">

                                Bulk Cohort Assignment

                            </p>

                            <p className="text-xs text-gray-500 mt-1">

                                Assign multiple Session Reference Content
                                items to a selected Organization, Trail,
                                Session and Cohort.

                            </p>

                        </div>

                        <button
                            type="button"
                            onClick={
                                toggleBulkAssign
                            }
                            disabled={
                                isBulkAssigning
                            }
                            aria-label="Close Bulk Assignment"
                            className="p-2 rounded-full hover:bg-gray-100 disabled:opacity-50"
                        >

                            <XMarkIcon
                                className="h-5 w-5 text-gray-600"
                            />

                        </button>

                    </div>



                    {/*
                    |--------------------------------------------------------------------------
                    | 37 - Destination Selectors
                    |--------------------------------------------------------------------------
                    */}

                    <div className="bg-orange-50 border border-orange-100 rounded-xl p-3">

                        <p className="text-xs font-bold text-gray-800 mb-3">

                            1. Select Assignment Destination

                        </p>

                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">


                            {/*
                            |--------------------------------------------------------------------------
                            | Organization
                            |--------------------------------------------------------------------------
                            */}

                            <div>

                                <label className="block text-xs font-semibold text-gray-700 mb-2">

                                    Organization

                                </label>

                                <select
                                    value={bulkOrgId}
                                    disabled={
                                        isBulkAssigning
                                    }
                                    onChange={event =>
                                        handleOrgChange(
                                            event.target.value
                                        )
                                    }
                                    className="w-full rounded bg-white border border-orange-200 p-2.5 text-xs"
                                >

                                    <option value="">

                                        Select Organization

                                    </option>

                                    {bulkOrgOptions.map(
                                        org => (

                                            <option
                                                key={
                                                    getRefId(org)
                                                }
                                                value={
                                                    getRefId(org)
                                                }
                                            >

                                                {org.organizationName}

                                            </option>

                                        )
                                    )}

                                </select>

                            </div>


                            {/*
                            |--------------------------------------------------------------------------
                            | Trail
                            |--------------------------------------------------------------------------
                            */}

                            <div>

                                <label className="block text-xs font-semibold text-gray-700 mb-2">

                                    Trail

                                </label>

                                <select
                                    value={bulkTrailId}
                                    disabled={
                                        isBulkAssigning ||
                                        !bulkOrgId
                                    }
                                    onChange={event =>
                                        handleTrailChange(
                                            event.target.value
                                        )
                                    }
                                    className="w-full rounded bg-white border border-orange-200 p-2.5 text-xs disabled:opacity-50"
                                >

                                    <option value="">

                                        Select Trail

                                    </option>

                                    {bulkTrailOptions.map(
                                        trail => (

                                            <option
                                                key={
                                                    getRefId(trail)
                                                }
                                                value={
                                                    getRefId(trail)
                                                }
                                            >

                                                {trail.trailName}

                                            </option>

                                        )
                                    )}

                                </select>

                            </div>


                            {/*
                            |--------------------------------------------------------------------------
                            | Session
                            |--------------------------------------------------------------------------
                            */}

                            <div>

                                <label className="block text-xs font-semibold text-gray-700 mb-2">

                                    Trinity Session

                                </label>

                                <select
                                    value={bulkSessionId}
                                    disabled={
                                        isBulkAssigning ||
                                        !bulkTrailId
                                    }
                                    onChange={event =>
                                        handleSessionChange(
                                            event.target.value
                                        )
                                    }
                                    className="w-full rounded bg-white border border-orange-200 p-2.5 text-xs disabled:opacity-50"
                                >

                                    <option value="">

                                        Select Session

                                    </option>

                                    {bulkSessionOptions.map(
                                        session => (

                                            <option
                                                key={
                                                    getRefId(session)
                                                }
                                                value={
                                                    getRefId(session)
                                                }
                                            >

                                                {
                                                    session.sessionSequence != null
                                                        ? `${session.sessionSequence} - `
                                                        : ""
                                                }

                                                {session.sessionName}

                                            </option>

                                        )
                                    )}

                                </select>

                            </div>


                            {/*
                            |--------------------------------------------------------------------------
                            | Cohort
                            |--------------------------------------------------------------------------
                            */}

                            <div>

                                <label className="block text-xs font-semibold text-gray-700 mb-2">

                                    Cohort

                                </label>

                                <select
                                    value={bulkCohortId}
                                    disabled={
                                        isBulkAssigning ||
                                        !bulkOrgId ||
                                        !bulkTrailId
                                    }
                                    onChange={event =>
                                        handleCohortChange(
                                            event.target.value
                                        )
                                    }
                                    className="w-full rounded bg-white border border-orange-200 p-2.5 text-xs disabled:opacity-50"
                                >

                                    <option value="">

                                        Select Cohort

                                    </option>

                                    {bulkCohortOptions.map(
                                        cohort => (

                                            <option
                                                key={
                                                    getRefId(cohort)
                                                }
                                                value={
                                                    getRefId(cohort)
                                                }
                                            >

                                                {
                                                    cohort.cohortName ||
                                                    cohort.cohortCode ||
                                                    "Unnamed Cohort"
                                                }

                                            </option>

                                        )
                                    )}

                                </select>

                            </div>


                        </div>


                        {!destinationReady && (

                            <p className="text-[11px] text-gray-500 mt-3">

                                Complete the destination selections
                                to view eligible reference content.

                            </p>

                        )}


                        {destinationReady && (

                            <p className="text-[11px] text-green-700 mt-3 font-semibold">

                                Destination selected. You can now
                                choose the content to assign.

                            </p>

                        )}

                    </div>



                    {/*
                    |--------------------------------------------------------------------------
                    | 38 - Reference Content Selection
                    |--------------------------------------------------------------------------
                    */}

                    {destinationReady && (

                        <div className="border border-gray-200 rounded-xl overflow-hidden">


                            {/*
                            |--------------------------------------------------------------------------
                            | Selection Header
                            |--------------------------------------------------------------------------
                            */}

                            <div className="bg-gray-50 border-b border-gray-200 p-3">

                                <div className="flex flex-wrap items-center justify-between gap-3">

                                    <div>

                                        <p className="text-xs font-bold text-gray-800">

                                            2. Select Reference Content

                                        </p>

                                        <p className="text-[11px] text-gray-500 mt-1">

                                            Only active reference content
                                            from the selected Trinity
                                            Session is displayed.

                                        </p>

                                    </div>

                                    <span className="rounded-full bg-orange-100 text-orange-700 px-3 py-1 text-xs font-bold">

                                        Selected: {
                                            bulkCounts.selected
                                        }

                                    </span>

                                </div>


                                {/*
                                |--------------------------------------------------------------------------
                                | Search
                                |--------------------------------------------------------------------------
                                */}

                                <div className="relative mt-3">

                                    <MagnifyingGlassIcon
                                        className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none"
                                    />

                                    <input
                                        type="text"
                                        value={
                                            bulkSearchText
                                        }
                                        disabled={
                                            isBulkAssigning
                                        }
                                        onChange={event =>
                                            setBulkSearchText(
                                                event.target.value
                                            )
                                        }
                                        placeholder="Search by title or description..."
                                        className="w-full rounded-lg bg-white border border-gray-200 py-2.5 pl-9 pr-9 text-xs focus:border-orange-400 outline-none disabled:opacity-50"
                                    />

                                    {bulkSearchText && (

                                        <button
                                            type="button"
                                            onClick={() =>
                                                setBulkSearchText("")
                                            }
                                            disabled={
                                                isBulkAssigning
                                            }
                                            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-orange-600"
                                        >

                                            <XMarkIcon
                                                className="h-4 w-4"
                                            />

                                        </button>

                                    )}

                                </div>



                                {/*
                                |--------------------------------------------------------------------------
                                | Selection Actions
                                |--------------------------------------------------------------------------
                                */}

                                <div className="flex flex-wrap items-center justify-between gap-2 mt-3">

                                    <div className="flex items-center gap-2">

                                        <button
                                            type="button"
                                            disabled={
                                                isBulkAssigning ||
                                                bulkCounts.available === 0
                                            }
                                            onClick={
                                                handleSelectAllVisible
                                            }
                                            className="rounded-full bg-orange-100 text-orange-700 px-3 py-1.5 text-[11px] font-bold hover:bg-orange-200 disabled:opacity-50"
                                        >

                                            Select All Visible

                                        </button>

                                        <button
                                            type="button"
                                            disabled={
                                                isBulkAssigning ||
                                                selectedContentIds.length === 0
                                            }
                                            onClick={
                                                handleClearSelection
                                            }
                                            className="rounded-full bg-gray-200 text-gray-700 px-3 py-1.5 text-[11px] font-bold hover:bg-gray-300 disabled:opacity-50"
                                        >

                                            Clear Selection

                                        </button>

                                    </div>

                                    <p className="text-[11px] text-gray-500">

                                        Showing {
                                            filteredReferenceContent.length
                                        } of {
                                            eligibleReferenceContent.length
                                        }

                                    </p>

                                </div>

                            </div>



                            {/*
                            |--------------------------------------------------------------------------
                            | 39 - Assignment Status Counts
                            |--------------------------------------------------------------------------
                            */}

                            <div className="flex flex-wrap gap-2 p-3 border-b border-gray-100">

                                <span className="rounded-full bg-gray-100 text-gray-700 px-3 py-1 text-[10px]">

                                    Total: {
                                        bulkCounts.total
                                    }

                                </span>

                                <span className="rounded-full bg-green-100 text-green-700 px-3 py-1 text-[10px]">

                                    Available: {
                                        bulkCounts.available
                                    }

                                </span>

                                <span className="rounded-full bg-orange-100 text-orange-700 px-3 py-1 text-[10px]">

                                    Already Assigned: {
                                        bulkCounts.assigned
                                    }

                                </span>

                                <span className="rounded-full bg-red-100 text-red-700 px-3 py-1 text-[10px]">

                                    Inactive Existing: {
                                        bulkCounts.inactive
                                    }

                                </span>

                            </div>



                            {/*
                            |--------------------------------------------------------------------------
                            | 40 - Scrollable Content Selection List
                            |--------------------------------------------------------------------------
                            */}

                            <div className="max-h-[460px] overflow-y-auto overscroll-contain p-3 space-y-2">


                                {isContentLoading && (

                                    <div className="p-4 text-center">

                                        <PulseLoader
                                            size={8}
                                            color="#ea580c"
                                        />

                                    </div>

                                )}


                                {isContentError && (

                                    <p className="text-xs text-red-700 bg-red-50 rounded p-3">

                                        {
                                            contentError?.data?.message ||
                                            "Unable to load Session Reference Content."
                                        }

                                    </p>

                                )}


                                {!isContentLoading &&
                                    !isContentError &&
                                    filteredReferenceContent.length === 0 && (

                                    <div className="bg-gray-50 rounded p-4">

                                        <p className="text-xs text-gray-600">

                                            No matching reference content found
                                            for this Trinity Session.

                                        </p>

                                    </div>

                                )}



                                {filteredReferenceContent.map(
                                    (content, index) => {

                                        const contentId =
                                            getRefId(content)

                                        const status =
                                            getAssignmentStatus(
                                                contentId
                                            )

                                        const isAvailable =
                                            status === "Available"

                                        const isSelected =
                                            selectedContentIds.includes(
                                                contentId
                                            )

                                        const ContentIcon =
                                            getContentIcon(
                                                content.contentType
                                            )

                                        const existingAssignment =
                                            existingAssignmentMap.get(
                                                contentId
                                            )

                                        return (

                                            <div
                                                key={contentId}
                                                className={`
                                                    rounded-lg
                                                    border
                                                    p-3
                                                    ${
                                                        isSelected
                                                            ? "bg-orange-50 border-orange-300"
                                                            : "bg-gray-50 border-gray-200"
                                                    }
                                                    ${
                                                        !isAvailable
                                                            ? "opacity-75"
                                                            : ""
                                                    }
                                                `}
                                            >

                                                <div className="flex items-start gap-3">


                                                    {/*
                                                    |--------------------------------------------------------------------------
                                                    | Selection Checkbox
                                                    |--------------------------------------------------------------------------
                                                    */}

                                                    <input
                                                        type="checkbox"
                                                        checked={
                                                            isSelected
                                                        }
                                                        disabled={
                                                            isBulkAssigning ||
                                                            !isAvailable
                                                        }
                                                        onChange={() =>
                                                            toggleContentSelection(
                                                                contentId
                                                            )
                                                        }
                                                        aria-label={
                                                            `Select ${content.title}`
                                                        }
                                                        className="h-4 w-4 mt-1 accent-orange-600 cursor-pointer disabled:cursor-not-allowed shrink-0"
                                                    />


                                                    <ContentIcon
                                                        className="h-5 w-5 text-orange-600 shrink-0"
                                                    />


                                                    <div className="flex-1 min-w-0">


                                                        <div className="flex flex-wrap items-start justify-between gap-2">

                                                            <p className="text-xs font-bold text-gray-800 break-words">

                                                                {
                                                                    content.displayOrder != null
                                                                        ? `${content.displayOrder} - `
                                                                        : `${index + 1} - `
                                                                }

                                                                {content.title}

                                                            </p>


                                                            <span className={`
                                                                rounded-full
                                                                px-2 py-1
                                                                text-[10px]
                                                                font-semibold
                                                                shrink-0
                                                                ${
                                                                    status === "Available"
                                                                        ? "bg-green-100 text-green-700"
                                                                        : status === "Assigned"
                                                                            ? "bg-orange-100 text-orange-700"
                                                                            : "bg-red-100 text-red-700"
                                                                }
                                                            `}>

                                                                {
                                                                    status === "Assigned"
                                                                        ? "Already Assigned"
                                                                        : status === "Inactive"
                                                                            ? "Inactive Existing"
                                                                            : "Available"
                                                                }

                                                            </span>

                                                        </div>



                                                        {/*
                                                        |--------------------------------------------------------------------------
                                                        | Description
                                                        |--------------------------------------------------------------------------
                                                        */}

                                                        {content.description && (

                                                            <p className="text-[11px] text-gray-600 mt-2 whitespace-pre-wrap leading-relaxed">

                                                                {content.description}

                                                            </p>

                                                        )}



                                                        {/*
                                                        |--------------------------------------------------------------------------
                                                        | Metadata
                                                        |--------------------------------------------------------------------------
                                                        */}

                                                        <div className="flex flex-wrap gap-2 mt-2">

                                                            <span className="bg-white rounded-full px-2 py-1 text-[10px] text-gray-500">

                                                                {content.contentType}

                                                            </span>


                                                            <span className="bg-white rounded-full px-2 py-1 text-[10px] text-gray-500">

                                                                Prompts: {
                                                                    (
                                                                        content.reflectionPrompts || []
                                                                    ).filter(
                                                                        prompt =>
                                                                            prompt.active !== false
                                                                    ).length
                                                                }

                                                            </span>

                                                        </div>



                                                        {/*
                                                        |--------------------------------------------------------------------------
                                                        | Inactive Existing Assignment
                                                        |--------------------------------------------------------------------------
                                                        */}

                                                        {status === "Inactive" && (

                                                            <div className="mt-3 bg-red-50 rounded p-2">

                                                                <p className="text-[11px] text-red-700">

                                                                    This content already has
                                                                    an inactive assignment
                                                                    for the selected cohort.
                                                                    Reactivate the existing
                                                                    assignment instead of
                                                                    creating a duplicate.

                                                                </p>

                                                                {existingAssignment && (

                                                                    <button
                                                                        type="button"
                                                                        disabled={
                                                                            isBulkAssigning
                                                                        }
                                                                        onClick={() =>
                                                                            onUpdate(
                                                                                getRefId(
                                                                                    existingAssignment
                                                                                )
                                                                            )
                                                                        }
                                                                        className="text-[11px] text-orange-700 font-bold mt-2 hover:underline"
                                                                    >

                                                                        Update Assignment

                                                                    </button>

                                                                )}

                                                            </div>

                                                        )}


                                                    </div>


                                                </div>

                                            </div>

                                        )

                                    }
                                )}


                            </div>



                            {/*
                            |--------------------------------------------------------------------------
                            | Selection Footer
                            |--------------------------------------------------------------------------
                            */}

                            <div className="bg-gray-50 border-t border-gray-200 p-3">

                                <div className="flex flex-wrap justify-between items-center gap-3">

                                    <p className="text-xs text-gray-600">

                                        <span className="font-bold text-orange-700">

                                            {
                                                bulkCounts.selected
                                            }

                                        </span>

                                        {" "}reference content items
                                        selected for assignment.

                                    </p>

                                    <button
                                        type="button"
                                        disabled={
                                            isBulkAssigning ||
                                            !destinationReady ||
                                            bulkCounts.selected === 0 ||
                                            isContentLoading ||
                                            isContentError ||
                                            bulkResults.length > 0
                                        }
                                        onClick={
                                            handleBulkAssign
                                        }
                                        className="
                                            inline-flex items-center gap-2
                                            rounded-lg
                                            bg-orange-600
                                            text-white
                                            px-5 py-2.5
                                            text-xs font-bold
                                            hover:bg-orange-700
                                            disabled:opacity-50
                                            disabled:cursor-not-allowed
                                        "
                                    >

                                        <RectangleStackOutlineIcon
                                            className="h-4 w-4"
                                        />

                                        {isBulkAssigning
                                            ? "Assigning..."
                                            : `Assign ${bulkCounts.selected} Selected`}

                                    </button>

                                </div>

                            </div>


                        </div>

                    )}



                    {/*
                    |--------------------------------------------------------------------------
                    | 41 - Bulk Processing Error
                    |--------------------------------------------------------------------------
                    */}

                    {bulkError && (

                        <div className="flex items-start gap-2 bg-red-50 border border-red-200 rounded-lg p-3">

                            <ExclamationTriangleIcon
                                className="h-5 w-5 text-red-500 shrink-0"
                            />

                            <p className="text-xs text-red-700">

                                {bulkError}

                            </p>

                        </div>

                    )}



                    {/*
                    |--------------------------------------------------------------------------
                    | 42 - Bulk Processing Progress
                    |--------------------------------------------------------------------------
                    */}

                    {bulkProgress && (

                        <div className="bg-gray-50 rounded-lg p-3 space-y-2">

                            <p className="text-xs font-bold text-gray-700">

                                {isBulkAssigning
                                    ? "Assignment in Progress"
                                    : bulkResults.length > 0
                                        ? "Assignment Processing Complete"
                                        : "Assignment Processing Stopped"}

                            </p>

                            <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">

                                <div
                                    className="h-full bg-orange-500 transition-all"
                                    style={{

                                        width:
                                            `${
                                                bulkProgress.total
                                                    ? (
                                                        bulkProgress.completed /
                                                        bulkProgress.total
                                                    ) * 100
                                                    : 0
                                            }%`,

                                    }}
                                />

                            </div>

                            <p className="text-[11px] text-gray-500">

                                {bulkProgress.completed}
                                {" "}of{" "}
                                {bulkProgress.total}
                                {" "}processed

                            </p>

                        </div>

                    )}



                    {/*
                    |--------------------------------------------------------------------------
                    | 43 - Bulk Assignment Results
                    |--------------------------------------------------------------------------
                    */}

                    {bulkResults.length > 0 && (

                        <div className="bg-gray-50 border border-gray-200 rounded-xl p-3 space-y-3">


                            <div className="flex items-center gap-2">

                                <CheckCircleIcon
                                    className="h-5 w-5 text-green-600"
                                />

                                <p className="text-sm font-bold text-gray-800">

                                    Bulk Assignment Complete

                                </p>

                            </div>



                            {/*
                            |--------------------------------------------------------------------------
                            | Results Summary
                            |--------------------------------------------------------------------------
                            */}

                            <div className="flex flex-wrap gap-3 text-xs">

                                <span className="text-green-700 font-bold">

                                    Created: {
                                        bulkResults.filter(
                                            result =>
                                                result.status === "Created"
                                        ).length
                                    }

                                </span>

                                <span className="text-orange-700 font-bold">

                                    Already Assigned: {
                                        bulkSkippedCounts?.assigned ?? 0
                                    }

                                </span>

                                <span className="text-red-700 font-bold">

                                    Inactive Existing: {
                                        bulkSkippedCounts?.inactive ?? 0
                                    }

                                </span>

                                <span className="text-red-700 font-bold">

                                    Failed: {
                                        bulkResults.filter(
                                            result =>
                                                result.status === "Failed"
                                        ).length
                                    }

                                </span>

                            </div>



                            {/*
                            |--------------------------------------------------------------------------
                            | Individual Results
                            |--------------------------------------------------------------------------
                            */}

                            <div className="max-h-[240px] overflow-y-auto space-y-2">

                                {bulkResults.map(
                                    (result, index) => (

                                        <div
                                            key={
                                                `${result.contentId}-${index}`
                                            }
                                            className="bg-white border border-gray-100 rounded p-2 flex flex-wrap justify-between items-start gap-2"
                                        >

                                            <div className="min-w-0 flex-1">

                                                <p className="text-xs font-semibold text-gray-700">

                                                    {result.title}

                                                </p>

                                                <p className="text-[10px] text-gray-500 mt-1">

                                                    {result.message}

                                                </p>

                                            </div>

                                            <span className={`
                                                text-[10px]
                                                font-bold
                                                ${
                                                    result.status === "Created"
                                                        ? "text-green-700"
                                                        : result.status === "Failed"
                                                            ? "text-red-700"
                                                            : "text-orange-700"
                                                }
                                            `}>

                                                {result.status}

                                            </span>

                                        </div>

                                    )
                                )}

                            </div>



                            {/*
                            |--------------------------------------------------------------------------
                            | Start Another Assignment
                            |--------------------------------------------------------------------------
                            */}

                            <button
                                type="button"
                                onClick={
                                    resetBulkSelection
                                }
                                className="bg-orange-100 text-orange-700 rounded px-4 py-2 text-xs font-bold hover:bg-orange-200"
                            >

                                Start Another Assignment

                            </button>


                        </div>

                    )}


                </div>

            )}



            {/*
            |--------------------------------------------------------------------------
            | 44 - Existing Assignment List
            |--------------------------------------------------------------------------
            */}

            {assignments.length === 0 && (

                <div className="bg-white rounded m-1 p-4">

                    <p className="text-sm text-gray-600">

                        No Session Content assignments found.
                        Click the add icon to configure cohort visibility,
                        or use Bulk Assign Content above.

                    </p>

                </div>

            )}



            {/*
            |--------------------------------------------------------------------------
            | 45 - Grouped Assignment Hierarchy
            |--------------------------------------------------------------------------
            */}

            {groupedAssignments.map(
                (org, orgIndex) => {

                    const orgKey =
                        `org-${org.id}`

                    return (

                        <div
                            key={org.id}
                            className="m-1 p-1 bg-white rounded"
                        >


                            {/*
                            |--------------------------------------------------------------------------
                            | Organization
                            |--------------------------------------------------------------------------
                            */}

                            <GroupHeader
                                groupKey={orgKey}
                                icon={BuildingOfficeIcon}
                                title={`${orgIndex + 1} - ${org.name}`}
                                count={org.count}
                                className="bg-gray-100 m-1"
                            />


                            {expanded[orgKey] &&

                                Object.values(
                                    org.trails
                                ).map(
                                    (trail, trailIndex) => {

                                        const trailKey =
                                            `${orgKey}-trail-${trail.id}`

                                        return (

                                            <div
                                                key={trailKey}
                                                className="bg-gray-100 rounded m-1 p-1"
                                            >


                                                {/*
                                                |--------------------------------------------------------------------------
                                                | Trail
                                                |--------------------------------------------------------------------------
                                                */}

                                                <GroupHeader
                                                    groupKey={trailKey}
                                                    icon={AcademicCapIcon}
                                                    title={`${trailIndex + 1} - ${trail.name}`}
                                                    count={trail.count}
                                                    className="bg-white m-1"
                                                />


                                                {expanded[trailKey] &&

                                                    Object.values(
                                                        trail.cohorts
                                                    ).map(
                                                        cohort => {

                                                            const cohortKey =
                                                                `${trailKey}-cohort-${cohort.id}`

                                                            return (

                                                                <div
                                                                    key={cohortKey}
                                                                    className="bg-white rounded m-1 p-1"
                                                                >


                                                                    {/*
                                                                    |--------------------------------------------------------------------------
                                                                    | Cohort
                                                                    |--------------------------------------------------------------------------
                                                                    */}

                                                                    <GroupHeader
                                                                        groupKey={cohortKey}
                                                                        icon={UserGroupIcon}
                                                                        title={cohort.name}
                                                                        count={cohort.count}
                                                                        className="bg-gray-100 m-1"
                                                                    />


                                                                    {expanded[cohortKey] &&

                                                                        Object.values(
                                                                            cohort.sessions
                                                                        ).map(
                                                                            session => {

                                                                                const sessionKey =
                                                                                    `${cohortKey}-session-${session.id}`

                                                                                const content =
                                                                                    session.assignments
                                                                                        .slice()
                                                                                        .sort(
                                                                                            (a, b) => {

                                                                                                const orderA =
                                                                                                    a.referenceContentId?.displayOrder ?? 0

                                                                                                const orderB =
                                                                                                    b.referenceContentId?.displayOrder ?? 0

                                                                                                return orderA - orderB

                                                                                            }
                                                                                        )

                                                                                return (

                                                                                    <div
                                                                                        key={sessionKey}
                                                                                        className="bg-gray-100 rounded m-1 p-1"
                                                                                    >


                                                                                        {/*
                                                                                        |--------------------------------------------------------------------------
                                                                                        | Session
                                                                                        |--------------------------------------------------------------------------
                                                                                        */}

                                                                                        <GroupHeader
                                                                                            groupKey={sessionKey}
                                                                                            icon={RectangleStackIcon}
                                                                                            title={session.name}
                                                                                            count={session.count}
                                                                                            className="bg-white m-1"
                                                                                        />


                                                                                        {expanded[sessionKey] && (

                                                                                            <div className="bg-white rounded m-1 p-1">

                                                                                                {content.map(
                                                                                                    (assignment, index) => (

                                                                                                        <div
                                                                                                            key={assignment._id}
                                                                                                            className="bg-gray-100 rounded m-1 p-2 flex justify-between items-center gap-2"
                                                                                                        >


                                                                                                            {/*
                                                                                                            |--------------------------------------------------------------------------
                                                                                                            | Assignment Content
                                                                                                            |--------------------------------------------------------------------------
                                                                                                            */}

                                                                                                            <div className="flex items-center min-w-0">

                                                                                                                <PlayCircleIcon
                                                                                                                    className="h-4 w-4 text-orange-600 mx-2 shrink-0"
                                                                                                                />

                                                                                                                <div className="min-w-0">

                                                                                                                    <p className="text-xs font-bold">

                                                                                                                        {index + 1} - {

                                                                                                                            assignment.referenceContentId?.title ||
                                                                                                                            "Reference Content not defined"

                                                                                                                        }

                                                                                                                    </p>

                                                                                                                    <p className="text-[10px] text-gray-500">

                                                                                                                        {
                                                                                                                            assignment.referenceContentId?.contentType ||
                                                                                                                            "Content"
                                                                                                                        }

                                                                                                                    </p>

                                                                                                                </div>


                                                                                                                <span className={`
                                                                                                                    text-[9px]
                                                                                                                    rounded-full
                                                                                                                    mx-2 px-2 py-1
                                                                                                                    ${
                                                                                                                        assignment.active !== false
                                                                                                                            ? "bg-lime-100 text-gray-700"
                                                                                                                            : "bg-red-100 text-red-700"
                                                                                                                    }
                                                                                                                `}>

                                                                                                                    {
                                                                                                                        assignment.active !== false
                                                                                                                            ? "Active"
                                                                                                                            : "Inactive"
                                                                                                                    }

                                                                                                                </span>

                                                                                                            </div>



                                                                                                            {/*
                                                                                                            |--------------------------------------------------------------------------
                                                                                                            | Existing Update / Delete Actions
                                                                                                            |--------------------------------------------------------------------------
                                                                                                            */}

                                                                                                            <div className="flex items-center shrink-0">

                                                                                                                <PencilSquareIcon
                                                                                                                    onClick={() =>
                                                                                                                        onUpdate(
                                                                                                                            assignment._id
                                                                                                                        )
                                                                                                                    }
                                                                                                                    className="h-4 w-4 text-orange-600 cursor-pointer mx-2"
                                                                                                                />

                                                                                                                <TrashIcon
                                                                                                                    onClick={() =>
                                                                                                                        onDelete(
                                                                                                                            assignment._id
                                                                                                                        )
                                                                                                                    }
                                                                                                                    className="h-4 w-4 text-orange-600 cursor-pointer mx-2"
                                                                                                                />

                                                                                                            </div>


                                                                                                        </div>

                                                                                                    )
                                                                                                )}

                                                                                            </div>

                                                                                        )}


                                                                                    </div>

                                                                                )

                                                                            }
                                                                        )

                                                                    }


                                                                </div>

                                                            )

                                                        }
                                                    )

                                                }


                                            </div>

                                        )

                                    }
                                )

                            }


                        </div>

                    )

                }
            )}


        </div>

    )

}


export default EtOrgTrailSessionContentAssignmentList